import type { SupabaseClient } from "@supabase/supabase-js";
import { DUPLICATE_EMAIL_MESSAGE, normalizeEmail, type SignUpInput } from "./auth";

/**
 * The prototype accepts 4 characters. Supabase's default minimum is 6.
 * Real sign-up uses a stricter minimum and still shows Supabase's own errors.
 */
export const SIGN_UP_PASSWORD_MIN = 8;

/** Names for the signup trigger. Role stays parent in the database. */
export function parentSignupMetadata(
  given: string,
  family: string,
  phone: string,
): { first_name: string; last_name: string; phone: string } {
  return {
    first_name: given.trim(),
    last_name: family.trim(),
    phone: phone.trim(),
  };
}

export type EmailSignUpResult =
  | { ok: true; status: "confirm_email"; email: string }
  | { ok: true; status: "signed_in"; email: string }
  | { ok: false; error: string };

export function signUpValidationError(input: SignUpInput): string | null {
  const given = input.given.trim();
  const family = input.family.trim();
  const email = normalizeEmail(input.email);
  if (!given || !family || !email) return "Fill in name and email.";
  if (!input.phone.trim()) return "Add a mobile number for guardian contact.";
  if (input.password.length < SIGN_UP_PASSWORD_MIN) {
    return `Password needs at least ${SIGN_UP_PASSWORD_MIN} characters.`;
  }
  if (input.password !== input.confirmPassword) {
    return "Passwords do not match.";
  }
  return null;
}

export async function signUpWithPassword(
  client: SupabaseClient | null,
  input: SignUpInput,
  options?: { emailRedirectTo?: string },
): Promise<EmailSignUpResult> {
  const validation = signUpValidationError(input);
  if (validation) return { ok: false, error: validation };
  if (!client) return { ok: false, error: "Sign-up is not configured." };

  const email = normalizeEmail(input.email);
  const data = parentSignupMetadata(input.given, input.family, input.phone);
  const { data: result, error } = await client.auth.signUp({
    email,
    password: input.password,
    options: {
      data,
      ...(options?.emailRedirectTo
        ? { emailRedirectTo: options.emailRedirectTo }
        : {}),
    },
  });
  if (error) return { ok: false, error: signUpErrorMessage(error.message) };
  if (!result.user) {
    return { ok: false, error: "Could not create that account." };
  }
  if (!result.session) {
    return { ok: true, status: "confirm_email", email };
  }
  return { ok: true, status: "signed_in", email };
}

export type EmailSignInResult =
  | { ok: true; email: string; given: string; family: string }
  | { ok: false; error: string };

function signUpErrorMessage(message: string): string {
  if (/already been registered|already registered|already exists/i.test(message)) {
    return DUPLICATE_EMAIL_MESSAGE;
  }
  return message;
}

function signInErrorMessage(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes("email not confirmed")) {
    return "Confirm your email before signing in. Check the inbox for the confirmation link.";
  }
  if (lower.includes("invalid login")) {
    return "Email or password does not match. For accounts you created on QA, use that password and confirm your email first. Dev shortcut rows use the password shown below.";
  }
  return message;
}

/** Signs in with Supabase. The profile role stays whatever the database stored. */
export async function signInWithPassword(
  client: SupabaseClient | null,
  email: string,
  password: string,
): Promise<EmailSignInResult> {
  const normalized = normalizeEmail(email);
  if (!normalized || !password) {
    return { ok: false, error: "Enter an email and password." };
  }
  if (!client) return { ok: false, error: "Sign-in is not configured." };

  const { data, error } = await client.auth.signInWithPassword({
    email: normalized,
    password,
  });
  if (error) return { ok: false, error: signInErrorMessage(error.message) };
  if (!data.user || !data.session) {
    return { ok: false, error: "Could not sign in." };
  }
  const meta = data.user.user_metadata ?? {};
  const given = String(meta.first_name ?? "").trim();
  const family = String(meta.last_name ?? "").trim();
  return {
    ok: true,
    email: normalizeEmail(data.user.email ?? normalized),
    given: given || "Parent",
    family: family || "Family",
  };
}

export async function updateParentPhoneMetadata(
  client: SupabaseClient,
  phone: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const trimmed = phone.trim();
  if (!trimmed) {
    return { ok: false, error: "Add a mobile number for guardian contact." };
  }
  const { error } = await client.auth.updateUser({ data: { phone: trimmed } });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}
